/**
 * App Version Policy API client.
 * Connects to admin-config-api-serverless via requestAppConfigAdmin.
 *
 * Note: the real API does not support `search` or `activeOnly` query params.
 * Those filters are omitted from API requests; the UI checkboxes for them
 * remain visible but have no effect on server-side filtering.
 */

import { requestAppConfigAdmin } from './auth';
import { PLATFORMS } from './appVersion';

export { PLATFORMS };
export const ENVIRONMENTS = ['DEV', 'TEST', 'PROD'];

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
}

/**
 * @param {{ page?: number, pageSize?: number, platform?: string, environment?: string, includeDeleted?: boolean }} [options]
 * @returns {Promise<{ items: object[], total_count: number, page: number, page_size: number }>}
 */
export async function listAppVersionPolicies(options = {}) {
  const { platform, environment, includeDeleted, page = 1, pageSize = 10 } = options;
  const query = buildQuery({
    platform: platform && platform !== 'ALL' ? platform : undefined,
    environment: environment && environment !== 'ALL' ? environment : undefined,
    include_deleted: includeDeleted ? 'true' : undefined,
    page,
    page_size: pageSize,
  });
  return requestAppConfigAdmin(`/api/v0/app/version_policies${query}`);
}

/**
 * @param {object} input
 * @param {{ actorUserId?: string }} [_opts] - ignored; JWT carries the actor identity
 * @returns {Promise<object>}
 */
export async function createAppVersionPolicy(input, _opts = {}) {
  return requestAppConfigAdmin('/api/v0/app/version_policies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * @param {number|string} id
 * @param {object} input
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<object>}
 */
export async function updateAppVersionPolicy(id, input, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/version_policies?id=${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Soft-deletes a policy (also sets is_active = 0 on the server).
 * @param {number|string} id
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<{ success: boolean }>}
 */
export async function deleteAppVersionPolicy(id, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/version_policies?id=${id}`, {
    method: 'DELETE',
  });
}

/**
 * Restores a soft-deleted policy via PATCH with deleted_at: "null".
 * @param {number|string} id
 * @param {{ actorUserId?: string }} [_opts]
 * @returns {Promise<object>}
 */
export async function restoreAppVersionPolicy(id, _opts = {}) {
  return requestAppConfigAdmin(`/api/v0/app/version_policies?id=${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ deleted_at: 'null' }),
  });
}
